import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CtmsWorkspaceModal from "../../../common/ctms/CtmsWorkspaceModal";
import {
  fetchAppointmentLocationLayout,
  fetchAppointmentLocationSites,
} from "../eplannerService";
import "./AppointmentLocationPicker.scss";

function idOf(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
}

function nameOf(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
}

function sameId(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

/** Match legacy RoomBox: Vacant + IsBlocked, else Occupied. */
function roomStatus(row) {
  const blocked = String(row?.IsBlocked ?? row?.isBlocked ?? "0").trim() === "1";
  if (blocked) return "blocked";
  const raw = String(row?.Status ?? row?.status ?? row?.RoomStatus ?? "").trim();
  if (/block/i.test(raw)) return "blocked";
  if (/occup/i.test(raw)) return "occupied";
  if (/vacant/i.test(raw) || !raw) return "vacant";
  return "occupied";
}

function capacityTypeLabel(row) {
  const raw = nameOf(row, "Type", "type", "CapacityType", "capacityType");
  if (/chair/i.test(raw)) return "Chair";
  return "Bed";
}

function formatSelectionLabel({
  siteName = "",
  buildingName = "",
  floorName = "",
  roomName = "",
  typeName = "",
}) {
  return [siteName, [buildingName, floorName].filter(Boolean).join(" / "), roomName, typeName]
    .filter(Boolean)
    .join(" / ");
}

function capacityKey(row) {
  return [
    idOf(row, "RoomTypeId", "roomTypeId"),
    idOf(row, "RoomId", "roomId"),
  ].join("|");
}

/** Match saved assignment against layout capacity rows (field names vary). */
function capacityMatchesWant(room, wantType, wantRoom) {
  const type = String(wantType || "").trim();
  const roomId = String(wantRoom || "").trim();
  if (type) {
    return (
      sameId(idOf(room, "RoomTypeId", "roomTypeId"), type) ||
      sameId(idOf(room, "RoomCapacityIdRef", "roomCapacityIdRef"), type) ||
      sameId(idOf(room, "TypeId", "typeId"), type) ||
      sameId(idOf(room, "CapacityId", "capacityId"), type)
    );
  }
  if (roomId) {
    return sameId(idOf(room, "RoomId", "roomId"), roomId);
  }
  return false;
}

function buildTreeSections(buildings, floors, rooms) {
  const sections = [];
  (buildings || []).forEach((building) => {
    const buildingId = idOf(building, "BuildingId", "buildingId");
    if (!buildingId) return;
    const buildingName =
      nameOf(building, "BuildingName", "buildingName") || "Building";
    const buildingFloors = (floors || []).filter((floor) =>
      sameId(
        idOf(floor, "BuildingRefId", "buildingRefId", "BuildingId"),
        buildingId,
      ),
    );
    const floorNodes = buildingFloors.map((floor) => {
      const floorId = idOf(floor, "FloorId", "floorId");
      const floorName = nameOf(floor, "FloorName", "floorName") || "Floor";
      const floorRooms = (rooms || []).filter((room) =>
        sameId(idOf(room, "FloorIdRef", "floorIdRef", "FloorId"), floorId),
      );
      const byRoom = new Map();
      floorRooms.forEach((room) => {
        const roomId = idOf(room, "RoomId", "roomId");
        if (!roomId) return;
        const key = roomId.toLowerCase();
        if (!byRoom.has(key)) {
          byRoom.set(key, {
            roomId,
            roomName: nameOf(room, "RoomName", "roomName") || "Room",
            capacities: [],
          });
        }
        byRoom.get(key).capacities.push(room);
      });
      return {
        floorId,
        floorName,
        rooms: Array.from(byRoom.values()),
      };
    });
    sections.push({
      buildingId,
      buildingName,
      floors: floorNodes,
    });
  });
  return sections;
}

function textMatches(value, query) {
  return String(value || "")
    .toLowerCase()
    .includes(query);
}

/** Keep matching Site → Building → Floor → Room branches for tree search. */
function filterTreeSections(tree, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return tree || [];
  return (tree || [])
    .map((building) => {
      const buildingMatch = textMatches(building.buildingName, q);
      const floors = (building.floors || [])
        .map((floor) => {
          const floorMatch = textMatches(floor.floorName, q);
          const rooms = buildingMatch || floorMatch
            ? floor.rooms || []
            : (floor.rooms || []).filter((room) =>
                textMatches(room.roomName, q),
              );
          if (!buildingMatch && !floorMatch && !rooms.length) return null;
          return { ...floor, rooms };
        })
        .filter(Boolean);
      if (!buildingMatch && !floors.length) return null;
      return { ...building, floors };
    })
    .filter(Boolean);
}

function treeHasQueryMatch(tree, query) {
  return filterTreeSections(tree, query).length > 0;
}

/** Strip "ABBR — Name" site picker labels down to the Capacity Management name. */
function capacitySiteNameFromLock({ lockedSiteName = "", lockedSiteLabel = "" }) {
  const direct = String(lockedSiteName || "").trim();
  if (direct) return direct;
  const label = String(lockedSiteLabel || "").trim();
  if (!label) return "";
  const parts = label.split(/\s+[—–]\s+/);
  if (parts.length >= 2) return parts.slice(1).join(" — ").trim();
  return label;
}

/**
 * Organisation SiteId (study Site dropdown) ≠ Capacity LocationId.
 * Match by id first, then by site name (same pattern as Capacity Management).
 */
function resolveLockedCapacitySite(sites = [], lockedSiteId = "", lockedSiteName = "") {
  const lockId = String(lockedSiteId || "").trim();
  const lockName = String(lockedSiteName || "").trim().toLowerCase();
  const list = Array.isArray(sites) ? sites : [];
  if (!lockId && !lockName) return null;

  const byId = list.find((row) =>
    sameId(idOf(row, "LocationId", "locationId"), lockId),
  );
  if (byId) return byId;

  if (lockName) {
    const byName = list.find(
      (row) =>
        String(nameOf(row, "LocationName", "locationName") || "")
          .trim()
          .toLowerCase() === lockName,
    );
    if (byName) return byName;
  }

  return null;
}

export default function AppointmentLocationPicker({
  open,
  onClose,
  onSelect,
  userData,
  studyId = "",
  /** Organisation SiteId from study Site dropdown (Tbl_OrganizationSite). */
  lockedSiteId = "",
  /** Organisation SiteName (or "ABBR — Name" label) for Capacity Location match. */
  lockedSiteName = "",
  visitId: _visitId = "",
  appointmentStartTime = "",
  slotDuration = 0,
  initialSelection = null,
}) {
  const [loadingSites, setLoadingSites] = useState(false);
  const [error, setError] = useState("");
  const [sites, setSites] = useState([]);
  /** siteId -> { loading, loaded, buildings, floors, rooms } */
  const [siteCache, setSiteCache] = useState({});
  const siteCacheRef = React.useRef({});
  const [expandedSites, setExpandedSites] = useState({});
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [expandedFloors, setExpandedFloors] = useState({});
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedRoomKey, setSelectedRoomKey] = useState("");
  const [treeSearch, setTreeSearch] = useState("");
  /**
   * Legacy picker defaults to Custom (all Capacity Management sites).
   * Default = study-mapped StudyLocations only — hides unmapped capacity sites.
   * When locked to a study Organisation site, use Custom so we can resolve
   * Org SiteId → Capacity LocationId (ids often differ; names match).
   */
  const [viewType, setViewType] = useState("Default");
  /** User hit Clear — do not re-apply saved assignment until picker reopens. */
  const userClearedRef = useRef(false);
  const appliedInitialKeyRef = useRef("");

  const resolvedLockName = useMemo(
    () =>
      capacitySiteNameFromLock({
        lockedSiteName,
        lockedSiteLabel: lockedSiteName,
      }),
    [lockedSiteName],
  );

  useEffect(() => {
    // Org SiteId is not a Capacity LocationId — need the full capacity list to resolve by name.
    if (lockedSiteId) setViewType("Custom");
  }, [lockedSiteId]);

  useEffect(() => {
    siteCacheRef.current = siteCache;
  }, [siteCache]);

  const siteOptions = useMemo(() => {
    const all = (sites || [])
      .map((row) => ({
        value: idOf(row, "LocationId", "locationId"),
        label: nameOf(row, "LocationName", "locationName") || "Site",
        row,
      }))
      .filter((option) => option.value);
    const lock = String(lockedSiteId || "").trim();
    if (!lock) return all;

    const matchedRow = resolveLockedCapacitySite(
      sites,
      lock,
      resolvedLockName,
    );
    if (matchedRow) {
      const value = idOf(matchedRow, "LocationId", "locationId");
      const label =
        nameOf(matchedRow, "LocationName", "locationName") ||
        resolvedLockName ||
        "Selected site";
      return [{ value, label, row: matchedRow }];
    }

    // No capacity location for this org site yet.
    return [
      {
        value: lock,
        label: resolvedLockName || "Selected site",
        row: {
          LocationId: lock,
          LocationName: resolvedLockName || "Selected site",
        },
      },
    ];
  }, [lockedSiteId, resolvedLockName, sites]);

  const searchQuery = treeSearch.trim().toLowerCase();

  const filteredSites = useMemo(() => {
    if (!searchQuery) return siteOptions;
    return siteOptions.filter((site) => {
      if (textMatches(site.label, searchQuery)) return true;
      const cache = siteCache[site.value];
      if (!cache || cache.loading) return true; // keep while loading deeper levels
      const tree = buildTreeSections(
        cache.buildings,
        cache.floors,
        cache.rooms,
      );
      return treeHasQueryMatch(tree, searchQuery);
    });
  }, [siteCache, siteOptions, searchQuery]);

  const selectedCapacity = useMemo(() => {
    if (!selectedRoomKey || !selectedRoom) return null;
    return (selectedRoom.capacities || []).find(
      (room) => capacityKey(room) === selectedRoomKey,
    );
  }, [selectedRoom, selectedRoomKey]);

  const detailCaps = useMemo(() => {
    if (!selectedRoom) return [];
    return selectedRoom.capacities || [];
  }, [selectedRoom]);

  const selectedPath = selectedRoom
    ? [
        selectedRoom.siteName,
        selectedRoom.buildingName,
        selectedRoom.floorName,
        selectedRoom.roomName,
      ]
        .filter(Boolean)
        .join(" / ")
    : "";

  const applyInitialRoomSelection = useCallback(
    (siteId, siteName, buildings, floors, rooms) => {
      if (userClearedRef.current) return false;
      const wantType = String(initialSelection?.roomTypeId || "").trim();
      const wantRoom = String(initialSelection?.roomId || "").trim();
      if (!wantType && !wantRoom) return false;
      const match = (rooms || []).find((room) =>
        capacityMatchesWant(room, wantType, wantRoom),
      );
      if (!match) return false;
      const roomId = idOf(match, "RoomId", "roomId");
      const floorId = idOf(match, "FloorIdRef", "floorIdRef", "FloorId");
      const buildingId = idOf(match, "BuildingId", "buildingId");
      const building = (buildings || []).find((b) =>
        sameId(idOf(b, "BuildingId", "buildingId"), buildingId),
      );
      const floor = (floors || []).find((f) =>
        sameId(idOf(f, "FloorId", "floorId"), floorId),
      );
      const roomCaps = (rooms || []).filter((r) =>
        sameId(idOf(r, "RoomId", "roomId"), roomId),
      );
      setSelectedRoom({
        siteId,
        siteName:
          siteName || nameOf(match, "LocationName", "locationName") || "",
        buildingId,
        buildingName: nameOf(building, "BuildingName", "buildingName"),
        floorId,
        floorName: nameOf(floor, "FloorName", "floorName"),
        roomId,
        roomName: nameOf(match, "RoomName", "roomName"),
        capacities: roomCaps,
      });
      setSelectedRoomKey(capacityKey(match));
      appliedInitialKeyRef.current = [
        String(siteId || "").toUpperCase(),
        wantType.toUpperCase(),
        wantRoom.toUpperCase(),
      ].join("|");
      return true;
    },
    [initialSelection?.roomId, initialSelection?.roomTypeId],
  );

  const findCachedSite = useCallback((siteId) => {
    const want = String(siteId || "").trim();
    if (!want) return null;
    const cache = siteCacheRef.current || {};
    const key = Object.keys(cache).find((entry) => sameId(entry, want));
    return key ? { key, cache: cache[key] } : null;
  }, []);

  const loadSiteLayout = useCallback(
    async (siteId, siteName = "") => {
      const cached = findCachedSite(siteId);
      if (cached?.cache?.loading) return cached.cache;
      if (cached?.cache?.loaded) {
        applyInitialRoomSelection(
          cached.key,
          siteName,
          cached.cache.buildings,
          cached.cache.floors,
          cached.cache.rooms,
        );
        return cached.cache;
      }
      setSiteCache((prev) => ({
        ...prev,
        [siteId]: {
          loading: true,
          loaded: false,
          buildings: prev[siteId]?.buildings || [],
          floors: prev[siteId]?.floors || [],
          rooms: prev[siteId]?.rooms || [],
        },
      }));
      try {
        const layout = await fetchAppointmentLocationLayout({
          userData,
          studyId,
          locationId: siteId,
          appointmentStartTime,
          slotDuration,
          viewType,
        });
        const buildings = layout.buildings || [];
        const floors = layout.floors || [];
        const rooms = layout.rooms || [];
        setSiteCache((prev) => ({
          ...prev,
          [siteId]: { loading: false, loaded: true, buildings, floors, rooms },
        }));

        const nextBuildings = {};
        const nextFloors = {};
        buildings.forEach((b) => {
          const bid = idOf(b, "BuildingId", "buildingId");
          if (bid) nextBuildings[bid] = true;
        });
        floors.forEach((f) => {
          const fid = idOf(f, "FloorId", "floorId");
          if (fid) nextFloors[fid] = true;
        });
        setExpandedBuildings((prev) => ({ ...prev, ...nextBuildings }));
        setExpandedFloors((prev) => ({ ...prev, ...nextFloors }));

        applyInitialRoomSelection(siteId, siteName, buildings, floors, rooms);

        if (!buildings.length && !rooms.length) {
          setError("No rooms found for this site.");
        } else {
          setError("");
        }
        return { buildings, floors, rooms };
      } catch (err) {
        setSiteCache((prev) => ({
          ...prev,
          [siteId]: {
            loading: false,
            loaded: true,
            buildings: [],
            floors: [],
            rooms: [],
          },
        }));
        setError(err?.message || "Could not load rooms.");
        return null;
      }
    },
    [
      applyInitialRoomSelection,
      appointmentStartTime,
      findCachedSite,
      slotDuration,
      studyId,
      userData,
      viewType,
    ],
  );

  // Search can match building/floor/room — prefetch site layouts when typing.
  useEffect(() => {
    if (!open || !searchQuery || !siteOptions.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const site of siteOptions) {
        if (cancelled) return;
        const cache = siteCacheRef.current[site.value];
        if (cache?.loading || cache?.loaded) continue;
        await loadSiteLayout(site.value, site.label);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSiteLayout, open, searchQuery, siteOptions]);

  useEffect(() => {
    if (!open) return;
    userClearedRef.current = false;
    appliedInitialKeyRef.current = "";
    setTreeSearch("");
    setSelectedRoom(null);
    setSelectedRoomKey("");
    setExpandedSites({});
    setExpandedBuildings({});
    setExpandedFloors({});
    setSiteCache({});
    // Match legacy: open on Custom so Capacity Management sites always appear.
    setViewType("Custom");
  }, [open, studyId]);

  // When assignment ids arrive after site layout (async child details), re-select.
  useEffect(() => {
    if (!open || userClearedRef.current) return;
    const siteId = String(initialSelection?.locationId || "").trim();
    const wantType = String(initialSelection?.roomTypeId || "").trim();
    const wantRoom = String(initialSelection?.roomId || "").trim();
    if (!siteId || (!wantType && !wantRoom)) return;
    const applyKey = [
      siteId.toUpperCase(),
      wantType.toUpperCase(),
      wantRoom.toUpperCase(),
    ].join("|");
    if (appliedInitialKeyRef.current === applyKey) return;
    const cached = findCachedSite(siteId);
    if (!cached?.cache?.loaded) return;
    const site = (sites || []).find((row) =>
      sameId(idOf(row, "LocationId", "locationId"), siteId),
    );
    setExpandedSites((prev) => ({ ...prev, [cached.key]: true }));
    applyInitialRoomSelection(
      cached.key,
      nameOf(site, "LocationName", "locationName"),
      cached.cache.buildings,
      cached.cache.floors,
      cached.cache.rooms,
    );
  }, [
    applyInitialRoomSelection,
    findCachedSite,
    initialSelection?.locationId,
    initialSelection?.roomId,
    initialSelection?.roomTypeId,
    open,
    siteCache,
    sites,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    if (!appointmentStartTime) {
      setError("Select appointment date and time before choosing a location.");
      setSites([]);
      return undefined;
    }
    if (!userData?.OrganizationId) {
      setError("Organization context missing. Please re-login.");
      setSites([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingSites(true);
      setError("");
      setSites([]);
      setSiteCache({});
      setSelectedRoom(null);
      setSelectedRoomKey("");
      setExpandedSites({});
      setExpandedBuildings({});
      setExpandedFloors({});
      try {
        const rows = await fetchAppointmentLocationSites({
          userData,
          studyId,
          appointmentStartTime,
          viewType,
        });
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setSites(list);
        if (!list.length) {
          setError(
            viewType === "Default"
              ? "No study-mapped sites for this study. Turn on “All sites” to see Capacity Management locations."
              : "No sites with capacity found for this organisation.",
          );
          return;
        }

        const matchedLocked = lockedSiteId
          ? resolveLockedCapacitySite(list, lockedSiteId, resolvedLockName)
          : null;
        const resolvedLockedId = matchedLocked
          ? idOf(matchedLocked, "LocationId", "locationId")
          : "";

        const initialSite =
          resolvedLockedId ||
          idOf({ LocationId: lockedSiteId }, "LocationId") ||
          idOf(
            {
              LocationId:
                initialSelection?.siteId || initialSelection?.locationId,
            },
            "LocationId",
          );
        const matchedInitial = list.find((row) =>
          sameId(idOf(row, "LocationId", "locationId"), initialSite),
        );
        const autoSite =
          matchedLocked ||
          matchedInitial ||
          (resolvedLockedId
            ? list.find((row) =>
                sameId(idOf(row, "LocationId", "locationId"), resolvedLockedId),
              )
            : null) ||
          (list.length === 1 ? list[0] : null);
        if (autoSite) {
          const sid = idOf(autoSite, "LocationId", "locationId");
          const sname = nameOf(autoSite, "LocationName", "locationName");
          setExpandedSites({ [sid]: true });
          if (!cancelled) await loadSiteLayout(sid, sname);
        } else if (lockedSiteId) {
          setError(
            resolvedLockName
              ? `No Capacity Management location found for “${resolvedLockName}”. Add buildings under that site in Capacity Management.`
              : "No capacity locations found for the selected study site.",
          );
        }
      } catch (err) {
        if (!cancelled) {
          setSites([]);
          setError(err?.message || "Could not load sites.");
        }
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    appointmentStartTime,
    initialSelection?.locationId,
    loadSiteLayout,
    open,
    lockedSiteId,
    resolvedLockName,
    studyId,
    userData,
    viewType,
  ]);

  const toggleSite = async (siteId, siteName) => {
    const willOpen = !expandedSites[siteId];
    setExpandedSites((prev) => ({ ...prev, [siteId]: willOpen }));
    if (!willOpen) return;
    const cache = siteCache[siteId];
    if (!cache || (!cache.buildings?.length && !cache.loading && !cache.rooms?.length)) {
      await loadSiteLayout(siteId, siteName);
    }
  };

  const selectRoomNode = ({
    siteId,
    siteName,
    buildingId,
    buildingName,
    floorId,
    floorName,
    room,
  }) => {
    setSelectedRoom({
      siteId,
      siteName,
      buildingId,
      buildingName,
      floorId,
      floorName,
      roomId: room.roomId,
      roomName: room.roomName,
      capacities: room.capacities || [],
    });
    setSelectedRoomKey("");
  };

  const handleConfirm = () => {
    if (!selectedCapacity || !selectedRoom) return;
    const buildingId =
      idOf(selectedCapacity, "BuildingId", "buildingId") ||
      selectedRoom.buildingId;
    const floorId =
      idOf(selectedCapacity, "FloorIdRef", "floorIdRef", "FloorId") ||
      selectedRoom.floorId;
    const roomId =
      idOf(selectedCapacity, "RoomId", "roomId") || selectedRoom.roomId;
    const roomTypeId = idOf(selectedCapacity, "RoomTypeId", "roomTypeId");
    const type = capacityTypeLabel(selectedCapacity);
    const label = formatSelectionLabel({
      siteName: selectedRoom.siteName || "",
      buildingName: selectedRoom.buildingName || "",
      floorName: selectedRoom.floorName || "",
      roomName: selectedRoom.roomName || "",
      typeName: nameOf(selectedCapacity, "RoomTypeName", "roomTypeName"),
    });
    onSelect?.({
      locationId:
        idOf(selectedCapacity, "LocationId", "locationId") || selectedRoom.siteId,
      siteId: lockedSiteId || selectedRoom.siteId,
      buildingId,
      floorId,
      roomId,
      roomTypeId,
      type,
      label,
    });
    onClose?.();
  };

  /** Clear only the bed/chair pick — keep site/building/floor/room tree as-is. */
  const handleClear = () => {
    userClearedRef.current = true;
    setSelectedRoomKey("");
    setError("");
  };

  return (
    <CtmsWorkspaceModal
      open={open}
      onClose={onClose}
      title="Choose your location"
      titleId="eplanner-location-picker-title"
      dialogClassName="ctms-workspace-modal__dialog--full eplanner-location-picker__dialog"
      contentClassName="eplanner-location-picker__content"
    >
      <div className="eplanner-location-picker">
        {!appointmentStartTime ? (
          <p className="eplanner-location-picker__hint">
            Select appointment date and time before choosing a location.
          </p>
        ) : (
          <div className="eplanner-location-picker__split">
            <section className="eplanner-location-picker__tree-panel">
              <header className="eplanner-location-picker__panel-head">
                <div>
                  <h3>Location tree</h3>
                  <p>Site → Building → Floor → Room</p>
                </div>
                {studyId && !lockedSiteId ? (
                  <label
                    className="eplanner-location-picker__view-toggle"
                    title="Off = study-mapped sites only. On = all Capacity Management sites."
                  >
                    <input
                      type="checkbox"
                      checked={viewType === "Custom"}
                      onChange={(event) =>
                        setViewType(event.target.checked ? "Custom" : "Default")
                      }
                    />
                    <span>All sites</span>
                  </label>
                ) : null}
              </header>

              <div className="eplanner-location-picker__search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search site, building, floor, room…"
                  value={treeSearch}
                  onChange={(e) => setTreeSearch(e.target.value)}
                />
              </div>

              <div className="eplanner-location-picker__tree-scroll">
                {loadingSites ? (
                  <p className="eplanner-location-picker__hint">Loading sites…</p>
                ) : null}
                {error && !filteredSites.length ? (
                  <p className="eplanner-location-picker__error">{error}</p>
                ) : null}
                {!loadingSites && !filteredSites.length && !error ? (
                  <p className="eplanner-location-picker__hint">
                    {searchQuery ? "No matching locations." : "No sites found."}
                  </p>
                ) : null}

                {filteredSites.map((site) => {
                  const siteId = site.value;
                  const cache = siteCache[siteId];
                  const fullTree = buildTreeSections(
                    cache?.buildings,
                    cache?.floors,
                    cache?.rooms,
                  );
                  const siteNameMatch = textMatches(site.label, searchQuery);
                  const tree = searchQuery
                    ? siteNameMatch
                      ? fullTree
                      : filterTreeSections(fullTree, searchQuery)
                    : fullTree;
                  const siteOpen =
                    Boolean(expandedSites[siteId]) || Boolean(searchQuery);

                  return (
                    <div key={siteId} className="eplanner-location-picker__node is-site">
                      <div className="eplanner-location-picker__node-row">
                        <button
                          type="button"
                          className="eplanner-location-picker__expand"
                          onClick={() => toggleSite(siteId, site.label)}
                          aria-expanded={siteOpen}
                        >
                          <i
                            className={`fas fa-chevron-${siteOpen ? "down" : "right"}`}
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          type="button"
                          className="eplanner-location-picker__node-label"
                          onClick={() => toggleSite(siteId, site.label)}
                        >
                          <i className="fas fa-hospital" aria-hidden="true" />
                          <span>{site.label}</span>
                        </button>
                      </div>

                      {siteOpen ? (
                        <div className="eplanner-location-picker__children">
                          {cache?.loading ? (
                            <p className="eplanner-location-picker__hint">Loading…</p>
                          ) : !tree.length ? (
                            <p className="eplanner-location-picker__hint">
                              {searchQuery
                                ? "No matching buildings / rooms."
                                : "No buildings / rooms for this site."}
                            </p>
                          ) : (
                            tree.map((building) => {
                              const buildingOpen =
                                Boolean(expandedBuildings[building.buildingId]) ||
                                Boolean(searchQuery);
                              return (
                                <div
                                  key={building.buildingId}
                                  className="eplanner-location-picker__node is-building"
                                >
                                  <div className="eplanner-location-picker__node-row">
                                    <button
                                      type="button"
                                      className="eplanner-location-picker__expand"
                                      onClick={() =>
                                        setExpandedBuildings((prev) => ({
                                          ...prev,
                                          [building.buildingId]: !buildingOpen,
                                        }))
                                      }
                                    >
                                      <i
                                        className={`fas fa-chevron-${buildingOpen ? "down" : "right"}`}
                                        aria-hidden="true"
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      className="eplanner-location-picker__node-label"
                                      onClick={() =>
                                        setExpandedBuildings((prev) => ({
                                          ...prev,
                                          [building.buildingId]: !buildingOpen,
                                        }))
                                      }
                                    >
                                      <i className="fas fa-building" aria-hidden="true" />
                                      <span>{building.buildingName}</span>
                                    </button>
                                  </div>

                                  {buildingOpen ? (
                                    <div className="eplanner-location-picker__children">
                                      {building.floors.map((floor) => {
                                        const floorOpen =
                                          Boolean(expandedFloors[floor.floorId]) ||
                                          Boolean(searchQuery);
                                        return (
                                          <div
                                            key={floor.floorId}
                                            className="eplanner-location-picker__node is-floor"
                                          >
                                            <div className="eplanner-location-picker__node-row">
                                              <button
                                                type="button"
                                                className="eplanner-location-picker__expand"
                                                onClick={() =>
                                                  setExpandedFloors((prev) => ({
                                                    ...prev,
                                                    [floor.floorId]: !floorOpen,
                                                  }))
                                                }
                                              >
                                                <i
                                                  className={`fas fa-chevron-${floorOpen ? "down" : "right"}`}
                                                  aria-hidden="true"
                                                />
                                              </button>
                                              <button
                                                type="button"
                                                className="eplanner-location-picker__node-label"
                                                onClick={() =>
                                                  setExpandedFloors((prev) => ({
                                                    ...prev,
                                                    [floor.floorId]: !floorOpen,
                                                  }))
                                                }
                                              >
                                                <i
                                                  className="fas fa-layer-group"
                                                  aria-hidden="true"
                                                />
                                                <span>{floor.floorName}</span>
                                              </button>
                                            </div>

                                            {floorOpen ? (
                                              <div className="eplanner-location-picker__children">
                                                {!floor.rooms.length ? (
                                                  <p className="eplanner-location-picker__hint">
                                                    No rooms
                                                  </p>
                                                ) : (
                                                  floor.rooms.map((room) => {
                                                    const isSelected =
                                                      selectedRoom &&
                                                      sameId(
                                                        selectedRoom.siteId,
                                                        siteId,
                                                      ) &&
                                                      sameId(
                                                        selectedRoom.roomId,
                                                        room.roomId,
                                                      );
                                                    const available = (
                                                      room.capacities || []
                                                    ).filter(
                                                      (cap) =>
                                                        roomStatus(cap) ===
                                                        "vacant",
                                                    ).length;
                                                    const total =
                                                      room.capacities.length;
                                                    return (
                                                      <div
                                                        key={room.roomId}
                                                        className={`eplanner-location-picker__node is-room${isSelected ? " is-selected" : ""}`}
                                                      >
                                                        <div className="eplanner-location-picker__node-row">
                                                          <span className="eplanner-location-picker__expand-spacer" />
                                                          <button
                                                            type="button"
                                                            className="eplanner-location-picker__node-label"
                                                            title={`${available} available of ${total}`}
                                                            onClick={() =>
                                                              selectRoomNode({
                                                                siteId,
                                                                siteName: site.label,
                                                                buildingId:
                                                                  building.buildingId,
                                                                buildingName:
                                                                  building.buildingName,
                                                                floorId: floor.floorId,
                                                                floorName:
                                                                  floor.floorName,
                                                                room,
                                                              })
                                                            }
                                                          >
                                                            <i
                                                              className="fas fa-door-open"
                                                              aria-hidden="true"
                                                            />
                                                            <span>{room.roomName}</span>
                                                            <em>
                                                              {available}/{total}
                                                            </em>
                                                          </button>
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                )}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="eplanner-location-picker__detail-panel">
              {!selectedRoom ? (
                <div className="eplanner-location-picker__detail-empty">
                  <i className="fas fa-bed" aria-hidden="true" />
                  <h3>Room capacity</h3>
                  <p>
                    Expand a site in the tree and select a <strong>room</strong> to
                    choose a vacant bed or chair.
                  </p>
                </div>
              ) : (
                <>
                  <header className="eplanner-location-picker__panel-head">
                    <div>
                      <h3>{selectedRoom.roomName || "Room"} capacity</h3>
                      <p title={selectedPath}>{selectedPath}</p>
                    </div>
                  </header>

                  <div className="eplanner-location-picker__capacity-stats">
                    <span>
                      <strong>
                        {
                          (selectedRoom.capacities || []).filter(
                            (c) => roomStatus(c) === "vacant",
                          ).length
                        }
                      </strong>{" "}
                      available
                    </span>
                    <span>
                      <strong>
                        {
                          (selectedRoom.capacities || []).filter(
                            (c) => capacityTypeLabel(c) === "Bed",
                          ).length
                        }
                      </strong>{" "}
                      beds
                    </span>
                    <span>
                      <strong>
                        {
                          (selectedRoom.capacities || []).filter(
                            (c) => capacityTypeLabel(c) === "Chair",
                          ).length
                        }
                      </strong>{" "}
                      chairs
                    </span>
                  </div>

                  <div className="eplanner-location-picker__legend">
                    <span>
                      <i className="is-vacant" /> Vacant
                    </span>
                    <span>
                      <i className="is-occupied" /> Occupied
                    </span>
                    <span>
                      <i className="is-blocked" /> Blocked
                    </span>
                  </div>

                  {!detailCaps.length ? (
                    <div className="eplanner-location-picker__detail-empty is-soft">
                      <p>No beds/chairs in this room.</p>
                    </div>
                  ) : (
                    <div className="eplanner-location-picker__capacity-grid">
                      {detailCaps.map((capacity) => {
                        const status = roomStatus(capacity);
                        const key = capacityKey(capacity);
                        const selected = selectedRoomKey === key;
                        const isChair = capacityTypeLabel(capacity) === "Chair";
                        const isCurrentAssignment = capacityMatchesWant(
                          capacity,
                          initialSelection?.roomTypeId,
                          initialSelection?.roomId,
                        );
                        // Keep current appointment bed selectable even when Occupied.
                        const disabled =
                          status !== "vacant" && !isCurrentAssignment;
                        const capName =
                          nameOf(capacity, "RoomTypeName", "roomTypeName") ||
                          capacityTypeLabel(capacity);
                        return (
                          <button
                            key={key}
                            type="button"
                            title={`${capName} · ${status}`}
                            disabled={disabled}
                            className={[
                              "eplanner-location-picker__capacity-card",
                              isChair ? "is-chair" : "is-bed",
                              `is-${status}`,
                              selected ? "is-selected" : "",
                              isCurrentAssignment ? "is-current" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => {
                              if (disabled) return;
                              setSelectedRoomKey(key);
                            }}
                          >
                            <span className="eplanner-location-picker__capacity-card-main">
                              {selected ? (
                                <i className="fas fa-check" aria-hidden="true" />
                              ) : (
                                <i
                                  className={
                                    isChair ? "fas fa-chair" : "fas fa-bed"
                                  }
                                  aria-hidden="true"
                                />
                              )}
                              <strong>{capName}</strong>
                            </span>
                            <span
                              className={`eplanner-location-picker__capacity-status is-${status}`}
                            >
                              {isCurrentAssignment && status === "occupied"
                                ? "Current"
                                : status}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        <div className="eplanner-location-picker__actions">
          <button type="button" className="btn btn-light" onClick={handleClear}>
            Clear
          </button>
          <button type="button" className="btn btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selectedCapacity}
            onClick={handleConfirm}
          >
            Select location
          </button>
        </div>
      </div>
    </CtmsWorkspaceModal>
  );
}
